/**
 * „Bewertung erbitten" — die kurze Standardmail im gewohnten Schreibfenster.
 *
 * Der Text kommt fertig vom Server (Vorlage aus den Einstellungen, dazu ein,
 * zwei Sätze von Clara aus dem Projektgedächtnis). Rechnet Clara noch, wartet
 * das Fenster kurz — man kann jederzeit ohne sie weiter. Gesendet wird von
 * Hand; danach ist die Bitte erledigt und die Aufgabe abgelegt.
 */
import { useEffect, useState } from "react";
import { fehlerText } from "@vimaana/ui";

import { useBewertungEntwurf, useBewertungGesendet } from "../../hooks/useBewertung";
import PostSchreiben, { type SchreibZiel } from "../projekt/PostSchreiben";
import "../../styles/post.css";

/** Länger wartet niemand gern vor einem leeren Fenster. */
const WARTEN_MS = 60_000;

type Props = {
  bewertungId: string;
  ziele: SchreibZiel[];
  onClose: () => void;
};

export default function BewertungSchreiben({ bewertungId, ziele, onClose }: Props) {
  const [warten, setWarten] = useState(true);
  const q = useBewertungEntwurf(bewertungId, warten);
  const gesendet = useBewertungGesendet();

  useEffect(() => {
    const t = window.setTimeout(() => setWarten(false), WARTEN_MS);
    return () => window.clearTimeout(t);
  }, []);

  const d = q.data;
  const clara = d?.clara;
  const zeigen = d && !(warten && clara === "unterwegs");

  if (!zeigen) {
    return (
      <div className="vg-schreib-schleier" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <section className="vg-schreib" role="dialog" aria-modal="true" aria-label="Bewertung erbitten">
          <header className="vg-schreib-kopf">
            <div>
              <p className="vg-schreib-ueber">Schreiben</p>
              <h2>Bewertung erbitten</h2>
            </div>
            <button type="button" className="vg-schreib-zu" onClick={onClose} aria-label="Schließen">×</button>
          </header>
          {q.error ? (
            <p className="vg-post-fehler">{fehlerText(q.error)}</p>
          ) : !d ? (
            <p className="vg-schreib-hinweis">Lädt …</p>
          ) : (
            <>
              <p className="vg-schreib-hinweis">
                Clara ergänzt gerade ein, zwei Sätze zum Projekt — das dauert meist unter einer Minute.
              </p>
              <footer className="vg-schreib-fuss">
                <span className="vg-schreib-von" />
                <button type="button" className="vg-btn" onClick={() => setWarten(false)}>Ohne Clara weiter</button>
              </footer>
            </>
          )}
        </section>
      </div>
    );
  }

  return (
    <PostSchreiben
      key={d.auftrag_id || "ohne-clara"}
      onClose={onClose}
      projektId={d.projekt_id || undefined}
      kundeId={d.kunde_id || undefined}
      ziele={ziele}
      an={d.an}
      betreff={d.betreff}
      text={d.text}
      entwurfId={d.auftrag_id || undefined}
      titel={d.titel || "Bewertung erbitten"}
      oben={clara === "aus" || (clara === "unterwegs" && !warten) ? (
        <p className="vg-schreib-hinweis">Clara ist gerade nicht erreichbar — der Text kommt ohne ihre persönlichen Sätze.</p>
      ) : null}
      onGesendet={() => gesendet.mutate(bewertungId)}
    />
  );
}
